import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  ACTIVATE_ANNOTATION_EVENT,
  ANNOTATABLE_SELECTION_CLEAR_EVENT,
  ANNOTATABLE_SELECTION_EVENT,
  CANCEL_CREATE_ANNOTATION_EVENT,
  CREATE_ANNOTATION_EVENT,
  CREATE_TMP_ANNOTATION_EVENT,
  REMOVE_ANNOTATION_EVENT
} from '@contently/editor';
import { uuid } from 'uuidv4';
import { useFetcher } from 'rest-hooks';
import AnnotationResource from '../resources/AnnotationResource';
import { updateAnnotationListCacheBust } from '../store/textEditorReducer';

function getRelativePosition(coords, positionContainerRef) {
  return function stateSetter() {
    const box = positionContainerRef.current.getBoundingClientRect();
    return coords.top - box.top - 14;
  };
}

export default function useTextEditorAnnotations(
  consumerApi,
  contentId,
  allContentIds,
  positionContainerRef
) {
  const [showCommentButton, setShowCommentButton] = useState(false);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [activeAnnotation, setActiveAnnotation] = useState(null);
  const [highlightedText, setHighlightedText] = useState(null);
  const [yPos, setYPos] = useState('0px');
  const createAnnotation = useFetcher(AnnotationResource.createShape());
  const { resolvedAnnotation } = useSelector(state => state.textEditor);
  const dispatch = useDispatch();

  useEffect(() => {
    consumerApi.on(ANNOTATABLE_SELECTION_EVENT, ({ range, coords, selectedText }) => {
      setYPos(getRelativePosition(coords, positionContainerRef));
      setShowCommentButton(true);
      setHighlightedText(selectedText);
    });
    consumerApi.on(CREATE_ANNOTATION_EVENT, markId => {
      setShowCommentForm(false);
      setActiveAnnotation(markId);
    });
    consumerApi.on(ANNOTATABLE_SELECTION_CLEAR_EVENT, () => setShowCommentButton(false));
    consumerApi.on(ACTIVATE_ANNOTATION_EVENT, ({ uuid, coords }) => {
      setYPos(getRelativePosition(coords, positionContainerRef));
      setActiveAnnotation(uuid);
    });
  }, [consumerApi, positionContainerRef]);

  useEffect(() => {
    if (resolvedAnnotation) {
      consumerApi.emit(REMOVE_ANNOTATION_EVENT, resolvedAnnotation);
      setActiveAnnotation(null);
    }
  }, [consumerApi, resolvedAnnotation]);

  const handleNewAnnotation = () => {
    consumerApi.emit(CREATE_TMP_ANNOTATION_EVENT, setShowCommentForm(true));
    setShowCommentButton(false);
  };

  const handleSubmitComment = async ({ body }) => {
    const markId = uuid();
    const data = {
      target_type: 'Content',
      target_id: contentId,
      type: 'Annotation::Text',
      mark_id: markId,
      highlightedText,
      comment_attributes: {
        body
      }
    };

    await createAnnotation({}, data, [
      [
        AnnotationResource.listShape(),
        { target_id: allContentIds, target_type: 'Content' },
        (newAnnotationId, annotationIds) => {
          return [...(annotationIds || []), newAnnotationId];
        }
      ]
    ]);
    dispatch(updateAnnotationListCacheBust(true));
    consumerApi.emit(CREATE_ANNOTATION_EVENT, markId);
  };

  const handleCancelComment = () => {
    consumerApi.emit(CANCEL_CREATE_ANNOTATION_EVENT, setShowCommentForm(false));
  };

  return {
    showCommentButton,
    setShowCommentButton,
    showCommentForm,
    activeAnnotation,
    setActiveAnnotation,
    handleNewAnnotation,
    handleSubmitComment,
    handleCancelComment,
    yPos
  };
}



// WEBPACK FOOTER //
// ./app/javascript/client/hooks/useTextEditorAnnotations.js